import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { format } from 'date-fns';
import { Add, Edit, Visibility, Clear } from '@mui/icons-material';
import { Autocomplete, Button, darken, debounce, Divider, Grid, IconButton, lighten, styled, TextField, Typography } from '@mui/material';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useUsuariosService } from '../../../../../use-cases/usuarios';
import { IUsuarioTable } from '../../../../../application/models/IUsuario';
import { SkeletonVelesTable } from '../../../../components/skeleton-veles';
import { useClientesService } from '../../../../../use-cases/clientes';
import { ICliente, IClienteTable, ITipoUsuario } from '../../../../../application/models';
import { buildClienteTextSearchFiltersFromTerm } from '../../../../../utils/cliente-busca-unificada';

const ListaUsuarios = () => {
  // State
  const { currentUser } = useAppSelector((state) => state.user);
  const { usuarios, clienteSelecionado } = useAppSelector((state) => state.usuarios);
  const { perfis } = useAppSelector((state) => state.perfil)
  const { clientes } = useAppSelector((state) => state.clientes)
  // Use State
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  type ICategoryOptions = 'nome' | 'email' | 'loginUsuario'
  const [category, setCategory] = useState<ICategoryOptions>('nome')
  const [inputValue, setInputValue] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const categoryOptions: ICategoryOptions[] = ['nome', 'email', 'loginUsuario']

  // Useful variables
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE

  const canEdit = isDemais || isTecnologiaDaInformacao || isCliente
  const canCreate = isDemais || isTecnologiaDaInformacao || isCliente

  // Service
  const {
    setUsuarios,
    setOpenCreateUsuarioForm,
    setUsuariosPagination,
    setResetClienteSelecionado,
    setClienteSelecionado,
    setOpenEditUsuarioForm,
    resetUsuarios
  } = useUsuariosService()
  const { setClientes, setClientesPagination, resetClientesList } = useClientesService()
  const lastFetchedClienteIdRef = useRef<number | null>(null)

  useEffect(() => {
    setResetClienteSelecionado()
    resetUsuarios()
    resetClientesList()
  }, [])

  useEffect(() => {
    if (currentUser && isCliente && !clienteSelecionado && clientes.length > 0) {
      const clienteDoUsuario = clientes.find(c => c.IdCliente === currentUser.IdCliente)

      if (clienteDoUsuario) {
        setClienteSelecionado({
          cliente: {
            Ativo: clienteDoUsuario.Ativo,
            Atualizacao: clienteDoUsuario.Atualizacao,
            CNPJ: clienteDoUsuario.CNPJ,
            IdCliente: clienteDoUsuario.IdCliente,
            Inclusao: clienteDoUsuario.Inclusao,
            LoginCliente: clienteDoUsuario.LoginCliente,
            NomeFantasia: clienteDoUsuario.NomeFantasia,
            RazaoSocial: clienteDoUsuario.RazaoSocial
          }
        })
      }
    }
  }, [currentUser, isCliente, clienteSelecionado, clientes, setClienteSelecionado])

  useEffect(() => {
    if (!clienteSelecionado) {
      lastFetchedClienteIdRef.current = null
      return
    }
    const idCliente = clienteSelecionado.IdCliente
    if (lastFetchedClienteIdRef.current === idCliente) return
    lastFetchedClienteIdRef.current = idCliente
    setUsuarios()
  }, [clienteSelecionado, setUsuarios])

  useEffect(() => {
    setClientesPagination({
      limit: 50,
      page: 1
    })

    if (currentUser) {
      if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: currentUser.IdUsuario,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS ||
        currentUser.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: currentUser.IdCliente
        });
      }
    }
  }, [setClientes, currentUser, setClientesPagination, setResetClienteSelecionado]);

  // Functions
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const resetUsuariosFilter = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    setInputValue('')
    setSearchValue('')
  }, [])

  const debounceSearchTerm = useCallback((searchTerm: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchValue(searchTerm)
      debounceTimerRef.current = null
    }, 300)
  }, [])

  const debounceSearchClientes = useMemo(() =>
    debounce((searchTerm: string) => {
      if (currentUser) {
        const baseFilters = {
          ...buildClienteTextSearchFiltersFromTerm(searchTerm),
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
        };

        if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
          setClientes({
            ...baseFilters,
            searchIdVendedor: currentUser.IdUsuario,
            SearchIdCliente: 0
          });
        } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS ||
          currentUser.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO) {
          setClientes({
            ...baseFilters,
            searchIdVendedor: 0,
            SearchIdCliente: 0
          });
        } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL) {
          setClientes({
            ...baseFilters,
            searchIdVendedor: 0,
            SearchIdCliente: 0
          });
        } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE) {
          setClientes({
            ...baseFilters,
            searchIdVendedor: 0,
            SearchIdCliente: 0
          });
        } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
          setClientes({
            ...baseFilters,
            searchIdVendedor: 0,
            SearchIdCliente: currentUser.IdCliente
          });
        }
      }
    }, 700),
    [currentUser, setClientes]
  )
  const categoryOptionsLabel = (categoryKey: ICategoryOptions) => {
    switch (categoryKey) {
      case 'nome':
        return 'Nome'
      case 'email':
        return 'Email'
      case 'loginUsuario':
        return 'Login Usuário'
      default:
        return 'Nome'
    }
  }

  const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === 'light'
        ? lighten(theme.palette.primary.light, 0.85)
        : darken(theme.palette.primary.main, 0.8),
  }));

  const GroupItems = styled('ul')({
    padding: 0,
  });

  // Filtro local para usuários
  const filteredUsuarios = useMemo(() => {
    if (!searchValue.trim()) return usuarios

    return usuarios.filter(usuario => {
      const searchTerm = searchValue.toLowerCase().trim()

      switch (category) {
        case 'nome':
          return usuario.Nome.toLowerCase().includes(searchTerm)
        case 'email':
          return usuario.Email.toLowerCase().includes(searchTerm)
        case 'loginUsuario':
          return usuario.LoginUsuario.toLowerCase().includes(searchTerm)
        default:
          return true
      }
    })
  }, [usuarios, searchValue, category])

  const columns = useMemo<MRT_ColumnDef<Omit<IUsuarioTable, "Senha">>[]>(() => {
    const columnsToSet: MRT_ColumnDef<Omit<IUsuarioTable, "Senha">>[] = [
      {
        id: 'editar',
        header: canEdit ? 'Editar' : 'Visualizar',
        size: 50,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              setOpenEditUsuarioForm({
                open: true,
                ativo: row.original.Ativo,
                email: row.original.Email,
                idUsuario: row.original.IdUsuario,
                nome: row.original.Nome,
                login: row.original.LoginUsuario,
                tipoUsuario: row.original.TipoUsuario,
                idPerfil: row.original.IdPerfil
              })
            }}
          >
            {
              canEdit ? <Edit />
                : <Visibility />
            }
          </IconButton>
        ),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Nome',
        header: 'Nome',
        size: 150,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'LoginUsuario',
        header: 'Login',
        size: 200,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Email',
        header: 'Email',
        size: 200,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'IdPerfil',
        header: 'Perfil',
        size: 150,
        accessorFn: (rowData: any) => {
          const perfil = perfis.find(p => p.IdPerfil === rowData.IdPerfil);
          return perfil ? perfil.Descricao : '-';
        },
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Ativo',
        header: 'Status',
        size: 100,
        accessorFn: (rowData: any) => {
          return rowData.Ativo ? 'Ativo' : 'Inativo';
        },
        Cell: ({ cell }) => {
          const isActive = cell.getValue<string>() === 'Ativo'

          return (
            <Typography
              color="Background"
              variant='caption'
              style={{
                display: 'inline-block',
                padding: '4px 8px',
                borderRadius: '8px',
                fontWeight: 'bold',
                backgroundColor: isActive ? 'green' : 'red',
                textAlign: 'center'
              }}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </Typography>
          )
        },
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Inclusao',
        header: 'Data de Inclusão',
        size: 150,
        accessorFn: (rowData: any) => {
          return new Date(rowData.Inclusao);
        },
        Cell: ({ cell }) => <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Atualizacao',
        header: 'Data de Atualização',
        size: 150,
        accessorFn: (rowData: any) => {
          return rowData.Atualizacao ? new Date(rowData.Atualizacao) : null
        },
        Cell: ({ cell }) => {
          if (cell.row.original.Atualizacao) {
            return <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>
          }
        },
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
    ]

    return columnsToSet
  }, [canEdit, setOpenEditUsuarioForm, perfis]);

  const table = useMaterialReactTable({
    columns,
    data: filteredUsuarios,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableExpandAll: false,
    enableDensityToggle: false,
    localization: MRT_Localization_PT_BR,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading: loading
    },
    rowCount: filteredUsuarios.length,
    manualPagination: false,
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 25, 50, 100],
      showFirstButton: true,
      showLastButton: true,
    }
  })

  useEffect(() => {
    setLoading(false)
  }, [])

  useEffect(() => {
    setUsuariosPagination({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize
    });
  }, [pagination.pageIndex, pagination.pageSize, setUsuariosPagination]);

  return (
    loading ? <SkeletonVelesTable /> :
      !clienteSelecionado ? (
        <>
          <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
            Usuários
          </Typography>
          <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />

          {/* Seleção de Cliente - Obrigatória */}
          <Grid container spacing={2} justifyContent={"space-between"} mb={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                autoComplete
                includeInputInList
                value={null}
                disabled={isCliente}
                filterOptions={(options) => options}
                options={[...clientes].sort((a, b) => {
                  const firstLetterA = (a.LoginCliente?.[0] || '').toUpperCase();
                  const firstLetterB = (b.LoginCliente?.[0] || '').toUpperCase();

                  if (firstLetterA !== firstLetterB) {
                    return firstLetterA.localeCompare(firstLetterB);
                  }

                  return (a.LoginCliente || '').localeCompare(b.LoginCliente || '');
                })}
                groupBy={(option) => (option.LoginCliente?.[0] || '').toUpperCase()}
                getOptionLabel={(option) => `${option.RazaoSocial} - ${option.LoginCliente}`}
                isOptionEqualToValue={(option, value) => option?.IdCliente === value?.IdCliente}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                onInputChange={(event, value, reason) => {
                  if (reason === 'input' && !isCliente) {
                    debounceSearchClientes(value)
                  }
                }}
                onChange={(event: any, newValue: IClienteTable | null) => {
                  if (!isCliente) {
                    if (newValue) {
                      setClienteSelecionado({
                        cliente: {
                          Ativo: newValue.Ativo,
                          Atualizacao: newValue.Atualizacao,
                          CNPJ: newValue.CNPJ,
                          IdCliente: newValue.IdCliente,
                          Inclusao: newValue.Inclusao,
                          LoginCliente: newValue.LoginCliente,
                          NomeFantasia: newValue.NomeFantasia,
                          RazaoSocial: newValue.RazaoSocial
                        }
                      })
                    } else {
                      setResetClienteSelecionado()
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isCliente ? 'Cliente' : 'Selecione um cliente'}
                    placeholder={isCliente ? 'Cliente selecionado automaticamente' : 'Login, razão social, nome fantasia ou CNPJ'}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
            Selecione um cliente para visualizar os usuários
          </Typography>
        </>
      ) : (
        <>
          <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
            Usuários
            {searchValue && (
              <Typography component="span" variant="body2" color="text.secondary" style={{ marginLeft: '8px' }}>
                ({filteredUsuarios.length} de {usuarios.length} usuários)
              </Typography>
            )}
          </Typography>
          <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />

          {/* Seleção de Cliente - Obrigatória */}
          <Grid container spacing={2} justifyContent={"space-between"} mb={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                autoComplete
                includeInputInList
                value={clienteSelecionado ?? null}
                disabled={isCliente}
                filterOptions={(options) => options}
                options={[...clientes].sort((a, b) => {
                  const firstLetterA = (a.LoginCliente?.[0] || '').toUpperCase();
                  const firstLetterB = (b.LoginCliente?.[0] || '').toUpperCase();

                  if (firstLetterA !== firstLetterB) {
                    return firstLetterA.localeCompare(firstLetterB);
                  }

                  return (a.LoginCliente || '').localeCompare(b.LoginCliente || '');
                })}
                groupBy={(option) => (option.LoginCliente?.[0] || '').toUpperCase()}
                getOptionLabel={(option) => `${option.RazaoSocial} - ${option.LoginCliente}`}
                isOptionEqualToValue={(option, value) => option?.IdCliente === value?.IdCliente}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                onInputChange={(event, value, reason) => {
                  if (reason === 'input' && !isCliente) {
                    debounceSearchClientes(value)
                  }
                }}
                onChange={(event: any, newValue: IClienteTable | ICliente | null) => {
                  if (!isCliente) {
                    if (newValue) {
                      setClienteSelecionado({
                        cliente: {
                          Ativo: newValue.Ativo,
                          Atualizacao: newValue.Atualizacao,
                          CNPJ: newValue.CNPJ,
                          IdCliente: newValue.IdCliente,
                          Inclusao: newValue.Inclusao,
                          LoginCliente: newValue.LoginCliente,
                          NomeFantasia: newValue.NomeFantasia,
                          RazaoSocial: newValue.RazaoSocial
                        }
                      })
                    } else {
                      setResetClienteSelecionado()
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isCliente ? 'Cliente' : 'Selecione um cliente'}
                    placeholder={isCliente ? 'Cliente selecionado automaticamente' : 'Login, razão social, nome fantasia ou CNPJ'}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container justifyContent={"space-between"} mb={2}>
            {
              canCreate && (
                <Grid item xs={4}>
                  <Button
                    style={{ marginBottom: '10px' }}
                    disabled={!clienteSelecionado}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setOpenCreateUsuarioForm({
                        openForm: true
                      });
                    }}
                  >
                    <Add style={{ marginRight: '8px' }} /> Novo Usuário
                  </Button>
                </Grid>
              )
            }
            <Grid item xs={canCreate ? 3 : 4}>
              <Autocomplete
                autoComplete
                includeInputInList
                options={categoryOptions}
                getOptionLabel={(option) => categoryOptionsLabel(option)}
                value={category}
                onChange={(event: any, newValue: ICategoryOptions | null) => {
                  if (newValue) {
                    setCategory(newValue)
                    setInputValue('')
                    setSearchValue('')
                  } else {
                    setCategory('nome')
                    setInputValue('')
                    setSearchValue('')
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Selecione uma categoria" />}
              />
            </Grid>
            <Grid item xs={canCreate ? 4 : 5}>
              <TextField
                fullWidth
                label={`Buscar por ${categoryOptionsLabel(category).toLowerCase()}`}
                variant="outlined"
                value={inputValue}
                onChange={(event) => {
                  const value = event.target.value
                  setInputValue(value)
                  debounceSearchTerm(value)
                }}
                placeholder={`Digite para filtrar por ${categoryOptionsLabel(category).toLowerCase()}`}
              />
            </Grid>
            <Grid item xs={canCreate ? 1 : 3}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={resetUsuariosFilter}
                disabled={!inputValue}
                style={{ height: '56px', minWidth: '56px' }}
                title="Limpar filtros"
              >
                <Clear />
              </Button>
            </Grid>
          </Grid>

          <MaterialReactTable table={table} />
        </>
      )
  );
};

export { ListaUsuarios };
